export enum ExportResultCode {
  SUCCESS = 0,
  FAILED = 1,
}

export interface ExportResult {
  code: ExportResultCode
  error?: string
}

type Callback<T> = (result: ExportResult, items: T[]) => void

export interface TelExporter<T> {
  export(items: T[], cb: (result: ExportResult) => void): void | Promise<void>
  shutdown?: () => Promise<void>
}

export type BatcherOptions = {
  // flush when queue reaches this size
  maxBatchSize?: number // default: 512
  // flush after this delay if batch not full
  scheduledDelayMs?: number // default: 5000
  // optional upper bound on queue size
  maxQueueSize?: number // default: 2048
}

export class Batcher<T> {
  private readonly exporter: TelExporter<T>
  private readonly onResult?: Callback<T>
  private readonly maxBatchSize: number
  private readonly scheduledDelayMs: number
  private readonly maxQueueSize: number

  private queue: T[] = []
  private timer: ReturnType<typeof setTimeout> | null = null
  private flushPromise: Promise<void> | null = null
  private closed = false

  constructor(
    exporter: TelExporter<T>,
    options: BatcherOptions = {},
    onResult?: Callback<T>,
  ) {
    this.exporter = exporter
    this.onResult = onResult
    this.maxBatchSize = options.maxBatchSize ?? 512
    this.scheduledDelayMs = options.scheduledDelayMs ?? 5000
    this.maxQueueSize = options.maxQueueSize ?? 2048
  }

  enqueue(item: T) {
    if (this.closed) return

    // backpressure: drop oldest when over capacity
    if (this.queue.length >= this.maxQueueSize) {
      this.queue.shift()
    }

    this.queue.push(item)

    if (this.queue.length >= this.maxBatchSize) {
      // immediate flush on threshold
      void this.flush()
    } else {
      this.schedule()
    }
  }

  async forceFlush(): Promise<void> {
    this.clearTimer()
    while (this.queue.length > 0 || this.flushPromise) {
      await (this.flushPromise ?? this.flush())
    }
  }

  async shutdown(): Promise<void> {
    this.closed = true
    this.clearTimer()
    await this.forceFlush()
    await this.exporter.shutdown?.()
  }

  private schedule() {
    if (this.timer) return
    this.timer = setTimeout(() => {
      this.timer = null
      void this.flush()
    }, this.scheduledDelayMs)
  }

  private clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  private flush(): Promise<void> {
    if (this.flushPromise) return this.flushPromise
    if (this.queue.length === 0) return Promise.resolve()

    this.clearTimer()

    const batch = this.queue.splice(0, this.maxBatchSize)

    const { promise, resolve } = Promise.withResolvers<void>()
    this.flushPromise = promise
    void this.exporter.export(batch, (result) => {
      try {
        this.onResult?.(result, batch)
      } finally {
        this.flushPromise = null
        if (this.queue.length >= this.maxBatchSize) {
          // batch threshold hit — drain immediately
          queueMicrotask(() => void this.flush())
        } else if (this.queue.length > 0) {
          // respect scheduledDelayMs between flushes to avoid
          // near-continuous exports under sustained traffic
          this.schedule()
        }
        resolve()
      }
    })
    return promise
  }
}
